import { ErrorMessage } from '@hookform/error-message';
import { cn } from '@workspace/utils';
import { Controller, FieldPath, FieldValues, get, RegisterOptions, useFormContext } from 'react-hook-form';
import type { SelectOption } from '../../types';
import { Field, LabelProps, TextError } from '../form';

export interface FieldRadioBlockProps<TFormValues extends FieldValues = any> extends LabelProps {
  name: FieldPath<TFormValues>;
  validation?: RegisterOptions<TFormValues, any>;
  options: SelectOption[];
  className?: string;
  classNameGroup?: string;
  disabled?: boolean;
}

/**
 * Groupe de radio buttons style "block" connecté au formulaire
 * - Boutons pleine largeur avec style block
 * - Validation automatique et gestion d'état
 * - Style actif avec fond coloré
 */
export function FieldRadioBlock({
  name,
  required = true,
  options,
  children,
  validation,
  className,
  classNameGroup,
  disabled,
}: FieldRadioBlockProps) {
  const methods = useFormContext<any>();

  const validate = validation?.validate
    ? typeof validation.validate === 'function'
      ? { validate: validation.validate }
      : validation.validate
    : {};

  // Extraire required pour éviter que la règle native de RHF traite `false` comme invalide
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { required: _required, ...restValidation } = validation || {};

  return (
    <Field className={className}>
      <Controller
        control={methods.control}
        name={name}
        rules={{
          ...restValidation,
          validate: {
            isRequired: (value) => {
              if (required && (value === undefined || value === null || value === '')) {
                return typeof validation?.required === 'string'
                  ? validation.required
                  : 'Ce champ est requis';
              }
              return true;
            },
            ...validate,
          },
        }}
        render={({ field }) => (
          <div className={cn('flex flex-col gap-2', classNameGroup)}>
            {options.map((option) => (
              <label
                key={option.value?.toString()}
                className={cn(
                  'flex items-center justify-center w-full py-3 px-4 rounded-lg border cursor-pointer transition-all duration-200 ease-in-out',
                  field.value === option.value
                    ? 'bg-radio-checked-bg text-radio-checked-text border-radio-checked-border'
                    : 'bg-radio-bg text-current border-radio-border hover:border-radio-hover-border hover:bg-radio-hover-bg hover:text-radio-hover-text',
                  (methods.formState.isSubmitting || option.disabled || disabled) && 'opacity-75 cursor-not-allowed',
                  get(methods.formState.errors, name) && 'border-radio-error-border'
                )}
              >
                <input
                  name={name}
                  type="radio"
                  className="sr-only"
                  value={option.value?.toString()}
                  checked={field.value === option.value}
                  onChange={() => {
                    if (!disabled && !option.disabled) {
                      option.onChange?.(option.value);
                      field.onChange(option.value);
                    }
                  }}
                  disabled={methods.formState.isSubmitting || option.disabled || disabled}
                />
                <span className="font-medium">{option.label}</span>
              </label>
            ))}
          </div>
        )}
      />
      <ErrorMessage
        errors={methods.formState.errors}
        name={name}
        render={({ message }) => <TextError className="text-center">{message}</TextError>}
      />
      {children}
    </Field>
  );
}
